/**
 * Clock Slider Javascript Controller
 * Handles rotation logic, counter-rotation, autoplay, and dynamic styles.
 */

class ClockSlider {
  constructor(container) {
    this.container = container;
    this.wheel = container.querySelector('.clock-slider__wheel');
    this.items = Array.from(container.querySelectorAll('.clock-slider__item'));
    this.slides = Array.from(container.querySelectorAll('.clock-slider__text-slide'));
    this.dots = Array.from(container.querySelectorAll('.clock-slider__dot'));
    this.prevBtn = container.querySelector('[data-nav="prev"]');
    this.nextBtn = container.querySelector('[data-nav="next"]');
    
    this.currentIndex = 0;
    this.totalSlides = this.items.length;
    if (this.totalSlides === 0) return;

    // Configuration from data attributes
    this.spacingAngle = parseFloat(container.dataset.spacingAngle) || 35; // Angle between products
    this.autoplaySpeed = (parseInt(container.dataset.autoplaySpeed) || 5) * 1000;
    this.autoplayEnabled = container.dataset.autoplay === 'true';
    this.autoplayTimer = null;

    this.init();
  }
  
  init() {
    // Set static angle positions for items
    this.items.forEach((item, index) => {
      // First item is at 0 degrees, next at spacingAngle, etc.
      const itemAngle = index * this.spacingAngle;
      item.style.setProperty('--item-angle', `${itemAngle}deg`);
      
      // Allow selecting a slide by clicking directly on its product image
      item.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });
    
    // Navigation arrows
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.prevSlide();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.nextSlide();
      });
    }
    
    // Dot indicator buttons
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(index);
      });
    });
    
    // Set up swipe gestures for mobile touch devices
    this.initTouchGestures();
    
    // Run dynamic styles & positions for slide 0
    this.updateUI();
    
    // Begin autoplay
    this.startAutoplay();
  }
  
  updateUI() {
    // Normalize current index to [0, totalSlides - 1]
    const activeIndexNormalized = ((this.currentIndex % this.totalSlides) + this.totalSlides) % this.totalSlides;

    // We rotate the wheel negatively by the active index's angle to bring it to center top
    const wheelRotation = -this.currentIndex * this.spacingAngle;
    this.container.style.setProperty('--wheel-rotation', `${wheelRotation}deg`);
    
    // Update active classes for products
    this.items.forEach((item, idx) => {
      if (idx === activeIndexNormalized) {
        item.classList.add('is-active');
      } else {
        item.classList.remove('is-active');
      }
    });
    
    // Update active classes for title and buttons content
    const activeSlideIndex = ((this.currentIndex % this.slides.length) + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, idx) => {
      if (idx === activeSlideIndex) {
        slide.classList.add('is-active');
      } else {
        slide.classList.remove('is-active');
      }
    });
    
    // Update active classes for bottom indicator dots
    this.dots.forEach((dot, idx) => {
      if (idx === activeSlideIndex) {
        dot.classList.add('is-active');
      } else {
        dot.classList.remove('is-active');
      }
    });
    
    // Dynamically update the HSL colors from block properties
    const activeItem = this.items[activeIndexNormalized];
    if (activeItem) {
      const bg = activeItem.dataset.bg || '#ffffff';
      const glow = activeItem.dataset.glow || 'rgba(255, 59, 92, 0.35)';
      const brandColor = activeItem.dataset.brandColor || '#ff3b5c';
      
      this.container.style.setProperty('--slider-bg', bg);
      this.container.style.setProperty('--slider-glow-color', glow);
      this.container.style.setProperty('--slider-brand-color', brandColor);
    }
  }
  
  getNearestIndex(targetIndex) {
    const N = this.totalSlides;
    const currentNormalized = this.currentIndex;
    
    // Compute the modulo difference
    let diff = (targetIndex - (currentNormalized % N)) % N;
    // Adjust JS negative modulos
    diff = (diff + N) % N;
    
    if (diff > N / 2) {
      diff -= N;
    }
    return currentNormalized + diff;
  }
  
  goToSlide(index, isRelative = false) {
    if (isRelative) {
      this.currentIndex = index;
    } else {
      this.currentIndex = this.getNearestIndex(index);
    }
    this.updateUI();
    this.resetAutoplay();
  }
  
  nextSlide() {
    this.goToSlide(this.currentIndex + 1, true);
  }
  
  prevSlide() {
    this.goToSlide(this.currentIndex - 1, true);
  }
  
  startAutoplay() {
    if (!this.autoplayEnabled) return;
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }
  
  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
  
  resetAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  initTouchGestures() {
    let startX = 0;
    let endX = 0;
    
    this.container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });
    
    this.container.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      const swipeDistance = endX - startX;
      
      // Swipe threshold is 50px
      if (Math.abs(swipeDistance) > 50) {
        if (swipeDistance > 0) {
          this.prevSlide();
        } else {
          this.nextSlide();
        }
      }
    }, { passive: true });
  }
}

// Instantiate ClockSlider on DOM ready and Shopify load events
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.clock-slider-section').forEach(container => {
    new ClockSlider(container);
  });
});

// Listen for custom Shopify Customizer loads
document.addEventListener('shopify:section:load', (event) => {
  const container = event.target.querySelector('.clock-slider-section');
  if (container) {
    new ClockSlider(container);
  }
});
